import {
  ChangeEvent,
  FocusEvent,
  HTMLAttributes,
  Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { InputRef } from 'antd';
import { Button, Spin } from '../Ant';
import { GeneralEmpty, Search } from '../icons';
import SkeletonLine from '../common/skeleton/skeletonLine';
import NoData from '../dashboard/noData';
import Tag from '../tag/tag';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { useActiveOption } from './useActiveOption';
import { useLoadMoreSentinel } from './useLoadMoreSentinel';
import { useRowHeight } from './useRowHeight';
import {
  AppliedFilters,
  EmptyState,
  FilterBar,
  LoadingMore,
  OptionDescription,
  OptionLabel,
  OptionList,
  OptionRow,
  Panel,
  SearchField,
  SkeletonRow,
} from './searchableSelectStyles';
import { SelectFilters, SelectOption } from './searchableSelectTypes';

export type PanelRootProps = Pick<
  HTMLAttributes<HTMLDivElement>,
  'onKeyDown' | 'onBlur' | 'tabIndex'
>;

export interface SearchableSelectPanelProps<V extends string | number> {
  ref?: Ref<HTMLDivElement>;
  rootProps: PanelRootProps;
  panelId: string;
  options: SelectOption<V>[];
  selectedValue?: V;
  onSelect: (option: SelectOption<V>) => void;
  ariaLabel: string;
  emptyText?: string;
  filters?: SelectFilters;
  onSearch?: (term: string) => void;
  searchPlaceholder?: string;
  onLoadMore?: () => void;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
}

const SKELETON_LABEL_WIDTHS = ['62%', '48%', '71%'];

function SearchableSelectPanel<V extends string | number>({
  ref,
  rootProps,
  panelId,
  options,
  selectedValue,
  onSelect,
  ariaLabel,
  emptyText,
  filters,
  onSearch,
  searchPlaceholder,
  onLoadMore,
  hasMore,
  loading,
  loadingMore,
}: Readonly<SearchableSelectPanelProps<V>>) {
  const { t } = useTranslation();
  const listboxId = useId();
  const [searchTerm, setSearchTerm] = useState('');
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null);
  const searchInputRef = useRef<InputRef>(null);
  const appliedTermRef = useRef('');
  const latestOnSearch = useRef(onSearch);
  const hasSearch = onSearch !== undefined && searchPlaceholder !== undefined;

  useEffect(() => {
    latestOnSearch.current = onSearch;
  });

  const applySearchTerm = useCallback((term: string) => {
    appliedTermRef.current = term;
    latestOnSearch.current?.(term);
  }, []);
  const { search } = useDebouncedSearch(applySearchTerm, { allowEmptySearch: true });

  useEffect(
    () => () => {
      if (appliedTermRef.current) latestOnSearch.current?.('');
    },
    [],
  );

  const getOptionId = (index: number) => `${listboxId}-option-${index}`;

  const { activeIndex, activeOptionId, setActiveIndex, handleKeyDown } = useActiveOption({
    options,
    preferredValue: searchTerm ? undefined : selectedValue,
    getOptionId,
    onPick: onSelect,
    handlesEdgeKeys: !hasSearch,
  });

  const setSentinel = useLoadMoreSentinel({
    scrollRoot: listElement,
    canLoadMore: hasMore && !loading && !loadingMore,
    itemCount: options.length,
    onLoadMore,
  });

  const handleRootFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (hasSearch) searchInputRef.current?.focus();
    else listElement?.focus();
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    search(event.target.value);
  };

  const pickOption = (option: SelectOption<V>) => {
    if (!option.disabled) onSelect(option);
  };

  /** Pointer movement, not entry: a list scrolled by keyboard under a still pointer must keep its active row. */
  const activateOnPointerMove = (option: SelectOption<V>, index: number) => {
    if (!option.disabled) setActiveIndex(index);
  };

  /** The tag or button that was clicked goes away with the change, which would leave focus on the page behind the panel. */
  const applyFilterChange = (change: () => void) => {
    change();
    searchInputRef.current?.focus();
  };

  const rowHeight = useRowHeight(options);
  const showEmptyText = !loading && options.length === 0;

  return (
    <Panel
      ref={ref}
      id={panelId}
      role="dialog"
      aria-label={ariaLabel}
      {...rootProps}
      onFocus={handleRootFocus}
    >
      {hasSearch && (
        <SearchField
          ref={searchInputRef}
          prefix={<Search />}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          role="combobox"
          aria-expanded
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          size="small"
          variant="outlined"
        />
      )}
      {filters && filters.applied.length > 0 && (
        <FilterBar>
          <AppliedFilters role="group" aria-label={t('common:filters')}>
            {filters.applied.map((filter) => (
              <Tag
                key={filter.value}
                text={filter.label}
                disabled={filter.disabled}
                disabledInfoMessage={filters.disabledInfoMessage}
                removeTag={() => applyFilterChange(() => filters.onRemove(filter.value))}
              />
            ))}
          </AppliedFilters>
          <Button
            data-testid="clear-filters-button"
            type="text"
            className="filter-cta"
            disabled={filters.clearAllDisabled}
            onClick={() => applyFilterChange(filters.onClearAll)}
          >
            {t('common:filter.clearAll')}
          </Button>
        </FilterBar>
      )}
      <OptionList
        ref={setListElement}
        id={listboxId}
        $rowHeight={rowHeight}
        role="listbox"
        aria-label={ariaLabel}
        aria-busy={loading || loadingMore}
        tabIndex={hasSearch ? undefined : -1}
        aria-activedescendant={hasSearch ? undefined : activeOptionId}
        onKeyDown={hasSearch ? undefined : handleKeyDown}
      >
        {loading
          ? SKELETON_LABEL_WIDTHS.map((labelWidth, index) => (
              <SkeletonRow key={`option-skeleton-${index}`} aria-hidden="true">
                <SkeletonLine $width={labelWidth} $height="14px" />
              </SkeletonRow>
            ))
          : options.map((option, index) => (
              <OptionRow
                key={option.value}
                id={getOptionId(index)}
                role="option"
                aria-selected={option.value === selectedValue}
                aria-disabled={option.disabled || undefined}
                data-active={index === activeIndex}
                onMouseMove={() => activateOnPointerMove(option, index)}
                onClick={() => pickOption(option)}
              >
                <span className="d-flex flex-col min-w-0">
                  <OptionLabel>{option.label}</OptionLabel>
                  {option.description && (
                    <OptionDescription className="lineclamp-2">
                      {option.description}
                    </OptionDescription>
                  )}
                </span>
              </OptionRow>
            ))}
        {hasMore && !loading && <div ref={setSentinel} aria-hidden="true" className="h-px" />}
        {loadingMore && (
          <LoadingMore aria-hidden="true">
            <Spin size="small" />
          </LoadingMore>
        )}
      </OptionList>
      <div role="status" aria-live="polite">
        {showEmptyText && (
          <EmptyState $rowHeight={rowHeight}>
            <NoData header={emptyText ?? t('common:noData')} icon={<GeneralEmpty />} />
          </EmptyState>
        )}
      </div>
    </Panel>
  );
}

export default SearchableSelectPanel;
